import { z } from "zod";

/**
 * Pure, side-effect-free SaaS metric calculations.
 *
 * Every function validates and clamps its input (no negative values, no NaN)
 * and returns a brand-new result object — inputs are never mutated.
 */

// Coerce strings from inputs into a finite, non-negative number.
const nonNegative = z.coerce.number().finite().min(0).catch(0);
// Percentages are stored as whole numbers (e.g. 5 = 5%).
const percent = z.coerce.number().finite().min(0).max(100).catch(0);

const MONTHS_PER_YEAR = 12;

// ---------- MRR ----------

export const mrrInputSchema = z.object({
  avgMrrPerCustomer: nonNegative,
  customers: nonNegative,
});

export type MrrInput = z.infer<typeof mrrInputSchema>;

export interface MrrResult {
  mrr: number;
  arr: number;
}

export function calculateMrr(input: MrrInput): MrrResult {
  const { avgMrrPerCustomer, customers } = mrrInputSchema.parse(input);
  const mrr = avgMrrPerCustomer * customers;
  return { mrr, arr: mrr * MONTHS_PER_YEAR };
}

// ---------- MRR from subscription plans ----------

export const subscriptionPlanSchema = z.object({
  // Total price the customer pays for one full subscription term.
  price: nonNegative,
  // Length of that term in months (1 = monthly, 12 = annual, ~0.23 = weekly).
  lengthMonths: z.coerce.number().finite().positive().catch(1),
  customers: nonNegative,
});

export type SubscriptionPlan = z.infer<typeof subscriptionPlanSchema>;

export interface PlanMrr {
  mrr: number;
}

export interface MrrFromPlansResult {
  mrr: number;
  arr: number;
  perPlan: PlanMrr[];
}

/**
 * Normalise a mix of subscription terms into a single MRR figure.
 * A plan's monthly value is price ÷ term length, times its customer count.
 */
export function calculateMrrFromPlans(plans: ReadonlyArray<SubscriptionPlan>): MrrFromPlansResult {
  const perPlan = plans.map((plan) => {
    const { price, lengthMonths, customers } = subscriptionPlanSchema.parse(plan);
    return { mrr: (price / lengthMonths) * customers };
  });
  const mrr = perPlan.reduce((sum, p) => sum + p.mrr, 0);
  return { mrr, arr: mrr * MONTHS_PER_YEAR, perPlan };
}

// ---------- ARR ----------

export const arrInputSchema = z.object({
  mrr: nonNegative,
});

export type ArrInput = z.infer<typeof arrInputSchema>;

export interface ArrResult {
  arr: number;
  daily: number;
}

export function calculateArr(input: ArrInput): ArrResult {
  const { mrr } = arrInputSchema.parse(input);
  const arr = mrr * MONTHS_PER_YEAR;
  return { arr, daily: arr / 365 };
}

// ---------- Churn ----------

export const churnInputSchema = z.object({
  customersStart: nonNegative,
  customersLost: nonNegative,
  mrrStart: nonNegative,
  mrrLost: nonNegative,
});

export type ChurnInput = z.infer<typeof churnInputSchema>;

export interface ChurnResult {
  customerChurnRate: number; // percent
  revenueChurnRate: number; // percent
  customerRetentionRate: number; // percent
}

export function calculateChurn(input: ChurnInput): ChurnResult {
  const { customersStart, customersLost, mrrStart, mrrLost } = churnInputSchema.parse(input);

  const lostCustomers = Math.min(customersLost, customersStart);
  const lostMrr = Math.min(mrrLost, mrrStart);

  const customerChurnRate = customersStart > 0 ? (lostCustomers / customersStart) * 100 : 0;
  const revenueChurnRate = mrrStart > 0 ? (lostMrr / mrrStart) * 100 : 0;

  return {
    customerChurnRate,
    revenueChurnRate,
    customerRetentionRate: 100 - customerChurnRate,
  };
}

// ---------- MRR growth projection ----------

export const mrrGrowthInputSchema = z.object({
  startingMrr: nonNegative,
  newMrrPerMonth: nonNegative,
  expansionRate: percent, // % of MRR added each month
  churnRate: percent, // % of MRR lost each month
  months: z.coerce.number().int().min(1).max(60).catch(12),
});

export type MrrGrowthInput = z.infer<typeof mrrGrowthInputSchema>;

export interface MrrGrowthPoint {
  month: number; // 0 = today
  mrr: number;
  arr: number;
}

export interface MrrGrowthResult {
  points: MrrGrowthPoint[];
  endingMrr: number;
  endingArr: number;
  netNewMrr: number; // total MRR gained over the horizon
}

export function calculateMrrGrowth(input: MrrGrowthInput): MrrGrowthResult {
  const { startingMrr, newMrrPerMonth, expansionRate, churnRate, months } =
    mrrGrowthInputSchema.parse(input);

  const expansion = expansionRate / 100;
  const churn = churnRate / 100;

  const points: MrrGrowthPoint[] = [
    { month: 0, mrr: startingMrr, arr: startingMrr * MONTHS_PER_YEAR },
  ];

  let mrr = startingMrr;
  for (let month = 1; month <= months; month += 1) {
    mrr = mrr + newMrrPerMonth + mrr * expansion - mrr * churn;
    mrr = Math.max(0, mrr);
    points.push({ month, mrr, arr: mrr * MONTHS_PER_YEAR });
  }

  return {
    points,
    endingMrr: mrr,
    endingArr: mrr * MONTHS_PER_YEAR,
    netNewMrr: mrr - startingMrr,
  };
}

// ---------- Customer lifetime value ----------

export const ltvInputSchema = z.object({
  arpa: nonNegative, // average monthly revenue per account, $
  grossMarginPct: percent,
  monthlyChurnPct: percent,
  cac: nonNegative, // optional in the UI — 0 means "not provided"
});

export type LtvInput = z.infer<typeof ltvInputSchema>;

export interface LtvResult {
  lifespanMonths: number; // expected customer lifetime, months
  ltv: number; // gross-margin-adjusted lifetime value, $
  ltvToCac: number | null; // ratio, null when no CAC provided
}

export function calculateLtv(input: LtvInput): LtvResult {
  const { arpa, grossMarginPct, monthlyChurnPct, cac } = ltvInputSchema.parse(input);

  const lifespanMonths = monthlyChurnPct > 0 ? 100 / monthlyChurnPct : 0;
  const ltv = arpa * (grossMarginPct / 100) * lifespanMonths;
  const ltvToCac = cac > 0 ? ltv / cac : null;

  return { lifespanMonths, ltv, ltvToCac };
}

// ---------- Net revenue retention ----------

export const nrrInputSchema = z.object({
  startingMrr: nonNegative, // cohort MRR at the start of the period
  expansionMrr: nonNegative,
  contractionMrr: nonNegative,
  churnedMrr: nonNegative,
});

export type NrrInput = z.infer<typeof nrrInputSchema>;

export interface NrrResult {
  nrr: number; // percent
  grr: number; // percent, capped at 100
  netRevenueChurn: number; // percent, negative = negative churn
}

export function calculateNrr(input: NrrInput): NrrResult {
  const { startingMrr, expansionMrr, contractionMrr, churnedMrr } = nrrInputSchema.parse(input);

  if (startingMrr <= 0) return { nrr: 0, grr: 0, netRevenueChurn: 0 };

  const losses = Math.min(contractionMrr + churnedMrr, startingMrr);
  const nrr = ((startingMrr + expansionMrr - losses) / startingMrr) * 100;
  const grr = ((startingMrr - losses) / startingMrr) * 100;

  return { nrr, grr, netRevenueChurn: 100 - nrr };
}

// ---------- CAC payback ----------

export const cacPaybackInputSchema = z.object({
  cac: nonNegative,
  monthlyRevenuePerCustomer: nonNegative,
  grossMarginPct: percent,
});

export type CacPaybackInput = z.infer<typeof cacPaybackInputSchema>;

export interface CacPaybackResult {
  monthlyMargin: number; // gross margin dollars per customer per month
  paybackMonths: number; // 0 when margin is zero
}

export function calculateCacPayback(input: CacPaybackInput): CacPaybackResult {
  const { cac, monthlyRevenuePerCustomer, grossMarginPct } = cacPaybackInputSchema.parse(input);

  const monthlyMargin = monthlyRevenuePerCustomer * (grossMarginPct / 100);
  const paybackMonths = monthlyMargin > 0 ? cac / monthlyMargin : 0;

  return { monthlyMargin, paybackMonths };
}

// ---------- Formatting helpers ----------

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const compactCurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 1,
  notation: "compact",
});

export function formatCurrency(value: number): string {
  if (!Number.isFinite(value)) return "$0";
  return currencyFormatter.format(value);
}

export function formatCompactCurrency(value: number): string {
  if (!Number.isFinite(value)) return "$0";
  return compactCurrencyFormatter.format(value);
}

export function formatPercent(value: number, fractionDigits = 1): string {
  if (!Number.isFinite(value)) return "0%";
  return `${value.toFixed(fractionDigits)}%`;
}
